import React, {useContext, useEffect, useState} from "react";
import Table, {TableActionBar, TableCell, TableRow} from "@amzn/meridian/table";
import Tooltip from "@amzn/meridian/tooltip";
import {FormattedMessage, injectIntl} from "react-intl";
import {getRefreshInterval} from "../util/Util";
import Button from "@amzn/meridian/button";
import {CycleManagementContext} from "./context/CycleManagementContext";
import Select, {SelectOption} from "@amzn/meridian/select";
import escapeRegExp from "lodash/escapeRegExp"
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert";
import {INDUCT_MANAGEMENT_FIELDS, SORTATION_TYPE} from "./Constants";
import {NEWLINE_CHAR} from "../util/Constants";
import InductProcessDetail from "../inductManagement/InductProcessDetail";

const CycleManagementTable = ({intl}) => {
    const {cycleViewModel, cycleDispatch} = useContext(CycleManagementContext)

    let cyclesInfo = Object.keys(cycleViewModel.cycleConfig)
        .map(index => cycleViewModel.cycleConfig[index].name + '~' + cycleViewModel.cycleConfig[index].assignedNode);

    const [searchNodeQuery, setSearchNodeQuery] = useState()
    const [searchCycleQuery, setSearchCycleQuery] = useState()

    const searchNodeRegExp = new RegExp(escapeRegExp(searchNodeQuery), "i")
    const searchCycleRegExp = new RegExp(escapeRegExp(searchCycleQuery), "i")
    const matchedNodeOptions = cycleViewModel.childNodes.filter(
        option => !searchNodeQuery || searchNodeRegExp.test(option)
    )
    const matchedCycleOptions = Object.keys(cycleViewModel.plannedCycles).filter(
        keys => !searchCycleQuery || searchCycleRegExp.test(cycleViewModel.plannedCycles[keys].name)
    )

    const getPlanDetails = async () => {
        cycleDispatch({
            type: 'LOAD_DRS_DATA',
            data: {dispatcher: cycleDispatch, cyclesInfo: cyclesInfo}
        })
    }

    const getForceData = () => {
        cycleDispatch({
            type: 'LOAD_VIEW',
            data: {dispatcher: cycleDispatch}
        })
    }

    const selectNode = (data) => {
        cycleDispatch({type: "SET_VALUE", data: {selectedCycleId: ""}})
        cycleDispatch({type: "SET_VALUE", data: {selectedNodeId: data}})
        cycleDispatch({
            type: 'LOAD_CYCLES',
            data: {dispatcher: cycleDispatch}
        })
    }

    const enableCycle = () => {
        if(cycleViewModel.selectedNodeId !== "" && cycleViewModel.selectedCycleId !== "") {
            let existingSTRCycle = null
            Object.values(cycleViewModel.cycleConfig).forEach(({type, name, assignedNode}) => {
                existingSTRCycle = (type === SORTATION_TYPE.SORT_TO_ROUTE
                    && assignedNode === cycleViewModel.selectedNodeId)? name : existingSTRCycle;
            })
            if(existingSTRCycle === null) {
                cycleDispatch({type: 'ENABLE_CYCLE', data: {dispatcher: cycleDispatch}})
            } else {
                cycleDispatch({type: 'SET_ALERT', data: {
                    id: "inductmanagement.activateSortToRouteError",
                    default: "ERROR: Cannot activate another SORT_TO_ROUTE type cycle. {cycle} is already active.",
                    values: {cycle: existingSTRCycle},
                    type: "error"
                }})
            }
        } else {
            cycleDispatch({type: 'SET_ALERT', data: {
                id: "inductmanagement.unselectedWarning",
                default: "Please select a node and cycle before clicking activate",
                values: {},
                type: "warning"}})
        }
    }

    const disableCycle = (index) => {
        cycleDispatch({
            type: 'DISABLE_CYCLE',
            data: {index: index, dispatcher: cycleDispatch}
        })
    }

    useEffect(() => {
        const interval = setInterval(() => {
            getForceData();
        }, getRefreshInterval());
        getPlanDetails();
        return () => clearInterval(interval);
    }, []);

    const getAlert = (alert) => {
        return (alert.id !== "" ?
            <Alert size="medium" type={alert.type}
                   onClose={() => cycleDispatch({type: "CLEAR_ALERT"})}>
                <FormattedMessage id={alert.id} defaultMessage={alert.default} values={alert.values}/>
            </Alert> : null)
    }

    const getTableHeader = () => {
        return(
            <TableRow header={true}>
                <TableCell>
                    <FormattedMessage id="inductmanagement.cycle" defaultMessage="Cycle"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="inductmanagement.assigned_node" defaultMessage="Assigned node"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="inductmanagement.force_induct" defaultMessage="Force induct"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="inductmanagement.active_until" defaultMessage="Active Until"/>
                </TableCell>
                <TableCell>
                    <Tooltip multiline={true} position={"top"} title={intl.formatMessage({id: "tooltip.dynamicCPlan"})}>
                        <FormattedMessage id="inductmanagement.dynamicCPlan" defaultMessage="Dynamic C.Plan"/>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="inductmanagement.disable_cycle" defaultMessage="Disable cycle"/>
                </TableCell>
            </TableRow>
        )
    }

    const getTableActionBar = () => {
        return (
            <TableActionBar widths={[200, 200, 200, "fill", 200]}>
                <Select
                    value={cycleViewModel.selectedNodeId}
                    onChange={(data) => {selectNode(data)}}
                    searchQuery={searchNodeQuery}
                    onSearch={setSearchNodeQuery}
                    placeholder={intl.formatMessage({
                        id:"inductmanagement.enterStationCode", default: "Enter Station Code..."})}
                    width={300}>

                    {matchedNodeOptions.map(option => (
                        <SelectOption
                            label={option}
                            value={option}
                            key={option}
                        />
                    ))}
                    {!matchedNodeOptions.length ? (
                        <Column alignmentVertical="center" spacing="100" spacingInset="100">
                            <Text alignment="center">
                                <FormattedMessage id={"inductmanagement.noStations"} default={"No stations available"}/>
                            </Text>
                        </Column>
                    ) : null}
                </Select>

                <Select
                    disabled={cycleViewModel.selectedNodeId === ""}
                    value={cycleViewModel.selectedCycleId}
                    onChange={(data) => {cycleDispatch({type: "SET_VALUE", data: {selectedCycleId: data}})}}
                    searchQuery={searchCycleQuery}
                    onSearch={setSearchCycleQuery}
                    placeholder={intl.formatMessage({id:"inductmanagement.enterCycle", default: "Enter Cycle..."})}
                    width={300}>

                    {matchedCycleOptions.map(key => (
                        <SelectOption
                            label={cycleViewModel.plannedCycles[key].name}
                            value={key}
                            key={key}
                        />
                    ))}
                    {!matchedCycleOptions.length ? (
                        <Column alignmentVertical="center" spacing="100" spacingInset="100">
                        <Text alignment="center">
                            <FormattedMessage id={"inductmanagement.noCycles"} default={"No cycles available"}/>
                        </Text>
                        </Column>) : null}
                </Select>

                <Select
                    disabled={cycleViewModel.selectedNodeId === "" || cycleViewModel.selectedCycleId === ""}
                    value={cycleViewModel.selectedTime}
                    onChange={(data) => {cycleDispatch({type: "SET_VALUE", data: {selectedTime: data}})}}
                    width={300}>

                    { Object.keys(cycleViewModel.cycleTimes).map(key => (
                        <SelectOption
                            label={key}
                            value={cycleViewModel.cycleTimes[key]}
                            key={key}
                        />
                    ))}
                </Select>

                <div />
                <Button type="primary" onClick={enableCycle}>
                    <FormattedMessage id={"inductmanagement.enableCycle"} default={"Enable cycle"}/>
                </Button>
            </TableActionBar>
        )
    }
    const splitCycleStatus = (str) => {
     return str.split(NEWLINE_CHAR)
                .map((item, index) => (index === 0) ? item : [<br key={index} />, item])
    }

    const getTableRows = () => {
        return (
            Object.keys(cycleViewModel.cycleConfig).map((index) => (
                <TableRow key={index}>
                    <TableCell header={true} key={"name"}> {cycleViewModel.cycleConfig[index].name} </TableCell>
                    {INDUCT_MANAGEMENT_FIELDS.map((field) => (
                        <TableCell key={field}> {cycleViewModel.cycleConfig[index][field]} </TableCell>
                    ))}
                    <TableCell key={index}>
                        {!cycleViewModel.cycleStatus[cycleViewModel.cycleConfig[index].name + '~' + cycleViewModel.cycleConfig[index].assignedNode]
                            ? null : 
                            splitCycleStatus(cycleViewModel.cycleStatus[cycleViewModel.cycleConfig[index].name + '~' + cycleViewModel.cycleConfig[index].assignedNode])
                        }
                    </TableCell>
                    <TableCell key={index}>
                        <Button onClick={() => disableCycle(index)}>
                            <FormattedMessage id={"inductmanagement.disableCycle"} default={"Disable cycle"}/>
                         </Button>
                    </TableCell>
                </TableRow>
            ))
        )
    }

    if (cycleViewModel.isNodeEnabled === false) {
        return (<InductProcessDetail/>)
    }
    else {
        return (
            <Column>
                {getAlert(cycleViewModel.alert)}
                <Table headerRows={1} spacing="small" showStripes={true}>
                    {getTableActionBar()}
                    {getTableHeader()}
                    {Object.keys(cycleViewModel.cycleConfig).length > 0 ? getTableRows() : null}
                </Table>

            </Column>
        )
    }
}

export default injectIntl(CycleManagementTable);


// WEBPACK FOOTER //
// ./src/components/inductmanagementv2/CycleManagementTable.js